import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  ReceiptText,
  RefreshCw,
  GraduationCap,
  Link2,
  UsersRound,
  ChevronLeft,
  ChevronRight,
  Printer,
  X,
  Copy,
  Check,
  TrendingUp,
} from 'lucide-react';
import EmptyState from '../components/EmptyState';
import Modal from '../components/Modal';
import { BrandSpinnerRing } from '../components/SiteBrandedLoader';
import { paymentsService } from '../services/api';

const formatToman = (tomanAmount: number): string =>
  Math.round(Number(tomanAmount) || 0).toLocaleString('fa-IR');

/** API آمار فروشنده: مبلغ لینک‌های پرداخت‌شده به ریال ذخیره شده؛ نمایش به تومان = ÷۱۰ */
const rialToToman = (rial: number): number => Math.round((Number(rial) || 0) / 10);

interface SalesPersonStats {
  salesPerson: {
    id: string;
    fullName: string;
    phone: string;
  };
  statistics: {
    totalLinks: number;
    paidLinks: number;
    unpaidLinks: number;
    totalRevenue: number;
    conversionRate: number;
    todayLinks: number;
    lastActivity: string | null;
  };
}

interface Invoice {
  id: string;
  invoiceNumber: string;
  amount: number;
  status: string;
  type: string;
  createdAt: string;
  description?: string | null;
  customerName?: string | null;
  customerPhone?: string | null;
  paidAt?: string | null;
  user?: {
    id: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    email?: string;
  };
  salesPerson?: {
    id: string;
    fullName: string;
    phone: string;
  } | null;
  paymentLink?: { linkCode?: string } | null;
}

type TabType = 'courses' | 'payment-links' | 'salespersons';

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'همه' },
  { value: 'PAID', label: 'پرداخت‌شده' },
  { value: 'PENDING', label: 'در انتظار' },
  { value: 'CREATED', label: 'ایجاد شده' },
  { value: 'GATEWAY_REDIRECTED', label: 'درگاه' },
  { value: 'PAYMENT_TIMEOUT', label: 'اتمام زمان' },
  { value: 'FAILED', label: 'ناموفق' },
  { value: 'CANCELLED', label: 'لغو' },
];

function normalizePagedInvoicesResponse(
  raw: unknown,
  defaultLimit: number,
): { data: Invoice[]; meta: { total?: number; page?: number; limit?: number; totalPages?: number } } {
  if (!raw || typeof raw !== 'object') {
    return { data: [], meta: { total: 0, page: 1, limit: defaultLimit, totalPages: 1 } };
  }
  const r = raw as Record<string, unknown>;
  const data = Array.isArray(r.data) ? r.data : Array.isArray(r.invoices) ? r.invoices : [];
  const items = data as Invoice[];
  if (r.meta && typeof r.meta === 'object') {
    return { data: items, meta: r.meta as Record<string, number | undefined> };
  }
  const p = r.pagination as Record<string, unknown> | undefined;
  const total = Number(p?.total) || 0;
  const page = Number(p?.page) || 1;
  const lim = Number(p?.limit) || defaultLimit;
  const totalPages =
    typeof p?.totalPages === 'number' ? p.totalPages : Math.max(1, Math.ceil(total / (lim || 1)));
  return { data: items, meta: { total, page, limit: lim, totalPages } };
}

function normalizeSalespersonStats(raw: unknown): {
  salesPersons: SalesPersonStats[];
  summary: Record<string, unknown>;
} {
  if (!raw || typeof raw !== 'object') {
    return { salesPersons: [], summary: {} };
  }
  const r = raw as { salesPersons?: SalesPersonStats[]; summary?: Record<string, unknown> };
  return {
    salesPersons: Array.isArray(r.salesPersons) ? r.salesPersons : [],
    summary:
      r.summary && typeof r.summary === 'object' && !Array.isArray(r.summary)
        ? r.summary
        : {},
  };
}

function getStatusStyle(status: string): { pill: string; dot: string } {
  switch (status) {
    case 'PAID':
      return {
        pill: 'bg-emerald-50 text-emerald-900 ring-1 ring-emerald-200/90',
        dot: 'bg-emerald-500',
      };
    case 'PENDING':
      return {
        pill: 'bg-amber-50 text-amber-950 ring-1 ring-amber-200/90',
        dot: 'bg-amber-500',
      };
    case 'FAILED':
    case 'PAYMENT_TIMEOUT':
      return {
        pill: 'bg-rose-50 text-rose-900 ring-1 ring-rose-200/90',
        dot: 'bg-rose-500',
      };
    case 'CANCELLED':
      return {
        pill: 'bg-slate-100 text-slate-700 ring-1 ring-slate-200/90',
        dot: 'bg-slate-400',
      };
    case 'CREATED':
    case 'GATEWAY_REDIRECTED':
      return {
        pill: 'bg-sky-50 text-sky-900 ring-1 ring-sky-200/90',
        dot: 'bg-sky-500',
      };
    default:
      return {
        pill: 'bg-slate-50 text-slate-800 ring-1 ring-slate-200/80',
        dot: 'bg-slate-400',
      };
  }
}

function getStatusText(status: string): string {
  const m: Record<string, string> = {
    PAID: 'پرداخت‌شده',
    PENDING: 'در انتظار',
    FAILED: 'ناموفق',
    CANCELLED: 'لغو شده',
    CREATED: 'ایجاد شده',
    GATEWAY_REDIRECTED: 'هدایت به درگاه',
    PAYMENT_TIMEOUT: 'اتمام زمان',
  };
  return m[status] || status;
}

function getTypeText(type: string): string {
  switch (type) {
    case 'COURSE_PURCHASE':
      return 'خرید دوره';
    case 'WALLET_CHARGE':
      return 'شارژ کیف';
    case 'PAYMENT_LINK':
      return 'لینک پرداخت';
    default:
      return type;
  }
}

function customerLabel(inv: Invoice): string {
  const n = inv.customerName?.trim();
  if (n) return n;
  const u = `${inv.user?.firstName || ''} ${inv.user?.lastName || ''}`.trim();
  return u || '—';
}

function customerPhone(inv: Invoice): string {
  return inv.customerPhone || inv.user?.phone || '—';
}

const formInputCls =
  'rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-900 shadow-sm outline-none transition focus:border-indigo-400 focus:ring-[3px] focus:ring-indigo-500/18';

const Invoices: React.FC = () => {
  const [activeTab, setActiveTab] = useState<TabType>('courses');
  const [courseInvoices, setCourseInvoices] = useState<{ data: Invoice[]; meta: Record<string, number | undefined> }>({
    data: [],
    meta: {},
  });
  const [paymentLinkInvoices, setPaymentLinkInvoices] = useState<{
    data: Invoice[];
    meta: Record<string, number | undefined>;
  }>({ data: [], meta: {} });
  const [salesPersonsStats, setSalesPersonsStats] = useState<{
    salesPersons: SalesPersonStats[];
    summary: Record<string, unknown>;
  }>({ salesPersons: [], summary: {} });
  const [pageLoading, setPageLoading] = useState(true);
  const [listRefreshing, setListRefreshing] = useState(false);
  const hasLoadedOnceRef = useRef(false);
  const [error, setError] = useState('');
  const [selectedSalesPerson, setSelectedSalesPerson] = useState<string>('');
  const [courseStatusFilter, setCourseStatusFilter] = useState<string>('');
  const [paymentLinkStatusFilter, setPaymentLinkStatusFilter] = useState<string>('');
  const [coursePage, setCoursePage] = useState(1);
  const [paymentLinkPage, setPaymentLinkPage] = useState(1);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [copied, setCopied] = useState(false);
  const limit = 20;

  const fetchData = useCallback(async () => {
    if (!hasLoadedOnceRef.current) setPageLoading(true);
    else setListRefreshing(true);
    setError('');
    try {
      const [courseData, paymentLinkData, statsData] = await Promise.all([
        paymentsService.getAllCourseInvoices({
          page: coursePage,
          limit,
          status: courseStatusFilter || undefined,
        }),
        paymentsService.getPaymentLinkInvoices({
          page: paymentLinkPage,
          limit,
          status: paymentLinkStatusFilter || undefined,
          salesPersonId: selectedSalesPerson || undefined,
        }),
        paymentsService.getSalesPersonsPaymentStats(),
      ]);

      setCourseInvoices(normalizePagedInvoicesResponse(courseData, limit));
      setPaymentLinkInvoices(normalizePagedInvoicesResponse(paymentLinkData, limit));
      setSalesPersonsStats(normalizeSalespersonStats(statsData));
    } catch (err: any) {
      setError(err.response?.data?.message || 'خطا در دریافت اطلاعات');
    } finally {
      setPageLoading(false);
      setListRefreshing(false);
      hasLoadedOnceRef.current = true;
    }
  }, [coursePage, paymentLinkPage, courseStatusFilter, paymentLinkStatusFilter, selectedSalesPerson]);

  useEffect(() => {
    void fetchData();
  }, [fetchData]);

  const courseRows = Array.isArray(courseInvoices.data) ? courseInvoices.data : [];
  const paymentRows = Array.isArray(paymentLinkInvoices.data) ? paymentLinkInvoices.data : [];
  const salesPersonRows = Array.isArray(salesPersonsStats.salesPersons) ? salesPersonsStats.salesPersons : [];

  const courseMeta = courseInvoices.meta;
  const paymentMeta = paymentLinkInvoices.meta;

  const pagePaidCount = useMemo(() => {
    const rows = activeTab === 'courses' ? courseRows : paymentRows;
    return rows.filter((r) => r.status === 'PAID').length;
  }, [activeTab, courseRows, paymentRows]);

  const pagePendingCount = useMemo(() => {
    const rows = activeTab === 'courses' ? courseRows : paymentRows;
    return rows.filter((r) => r.status === 'PENDING' || r.status === 'CREATED' || r.status === 'GATEWAY_REDIRECTED')
      .length;
  }, [activeTab, courseRows, paymentRows]);

  const pageVolumeToman = useMemo(() => {
    const rows = activeTab === 'courses' ? courseRows : paymentRows;
    return rows.filter((r) => r.status === 'PAID').reduce((s, r) => s + (Number(r.amount) || 0), 0);
  }, [activeTab, courseRows, paymentRows]);

  const tabs = useMemo(
    () => [
      { id: 'courses' as TabType, label: 'فاکتور دوره', Icon: GraduationCap, count: courseMeta?.total ?? 0 },
      { id: 'payment-links' as TabType, label: 'فاکتور لینک فروش', Icon: Link2, count: paymentMeta?.total ?? 0 },
      { id: 'salespersons' as TabType, label: 'آمار فروشندگان', Icon: UsersRound, count: salesPersonRows.length },
    ],
    [courseMeta?.total, paymentMeta?.total, salesPersonRows.length],
  );

  const currentPage = activeTab === 'courses' ? coursePage : paymentLinkPage;
  const totalPages = Math.max(
    1,
    Number(activeTab === 'courses' ? courseMeta?.totalPages : paymentMeta?.totalPages) || 1,
  );
  const totalCount = Number(activeTab === 'courses' ? courseMeta?.total : paymentMeta?.total) || 0;

  const setCurrentPage = (p: number) => {
    if (activeTab === 'courses') setCoursePage(p);
    else setPaymentLinkPage(p);
  };

  const statusFilter = activeTab === 'courses' ? courseStatusFilter : paymentLinkStatusFilter;
  const setStatusFilter = (v: string) => {
    if (activeTab === 'courses') {
      setCourseStatusFilter(v);
      setCoursePage(1);
    } else {
      setPaymentLinkStatusFilter(v);
      setPaymentLinkPage(1);
    }
  };

  const activeFilterCount =
    (statusFilter ? 1 : 0) + (activeTab === 'payment-links' && selectedSalesPerson ? 1 : 0);

  const handleCopyNumber = async (num: string) => {
    try {
      await navigator.clipboard.writeText(num);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };

  const renderPagination = () => {
    if (activeTab === 'salespersons' || totalPages <= 1) return null;
    return (
      <div className="flex flex-col gap-3 rounded-2xl border border-slate-200/90 bg-white px-4 py-4 shadow-sm sm:flex-row sm:items-center sm:justify-between sm:py-3.5">
        <div className="text-sm font-semibold text-slate-700">
          صفحه <span className="font-black text-indigo-800">{currentPage.toLocaleString('fa-IR')}</span> از{' '}
          <span className="font-black text-slate-900">{totalPages.toLocaleString('fa-IR')}</span>
          <span className="mx-2 text-slate-400">·</span>
          <span className="text-slate-600">{totalCount.toLocaleString('fa-IR')} ردیف</span>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage <= 1}
            className="inline-flex items-center gap-1 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-800 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-45"
          >
            <ChevronRight className="h-4 w-4" aria-hidden />
            قبلی
          </button>
          <button
            type="button"
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage >= totalPages}
            className="inline-flex items-center gap-1 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-800 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-45"
          >
            بعدی
            <ChevronLeft className="h-4 w-4" aria-hidden />
          </button>
        </div>
      </div>
    );
  };

  const renderInvoiceTable = (rows: Invoice[], mode: 'courses' | 'payment-links') => (
    <div className="relative overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-sm ring-1 ring-slate-100">
      {listRefreshing ? (
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center bg-white/55 backdrop-blur-[1px]">
          <BrandSpinnerRing size="md" />
        </div>
      ) : null}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[720px] table-fixed divide-y divide-slate-200/80 text-sm">
          <thead className="bg-gradient-to-b from-slate-100/90 to-slate-50/95">
            <tr>
              <th className="w-[8.5rem] px-3 py-3.5 text-right text-xs font-bold text-slate-700">شماره فاکتور</th>
              <th className="px-3 py-3.5 text-right text-xs font-bold text-slate-700">مشتری</th>
              <th className="w-32 px-2 py-3.5 text-right text-xs font-bold text-slate-700">موبایل</th>
              <th className="w-28 px-2 py-3.5 text-center text-xs font-bold text-slate-700">مبلغ (تومان)</th>
              <th className="w-28 px-2 py-3.5 text-center text-xs font-bold text-slate-700">نوع</th>
              <th className="w-32 px-2 py-3.5 text-center text-xs font-bold text-slate-700">وضعیت</th>
              <th className="w-28 px-2 py-3.5 text-right text-xs font-bold text-slate-700">تاریخ</th>
              {mode === 'payment-links' ? (
                <th className="w-36 px-2 py-3.5 text-right text-xs font-bold text-slate-700">فروشنده</th>
              ) : null}
              <th className="w-24 px-2 py-3.5 text-center text-xs font-bold text-slate-700">جزئیات</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 bg-white">
            {rows.map((inv, idx) => {
              const st = getStatusStyle(inv.status);
              return (
                <tr
                  key={inv.id}
                  className={`transition-colors hover:bg-indigo-50/50 ${idx % 2 === 1 ? 'bg-slate-50/40' : ''}`}
                >
                  <td className="px-3 py-3.5 align-middle">
                    <span className="font-mono text-xs font-bold text-slate-900">#{inv.invoiceNumber}</span>
                  </td>
                  <td className="px-3 py-3.5 align-middle">
                    <div className="truncate font-semibold text-slate-900" title={customerLabel(inv)}>
                      {customerLabel(inv)}
                    </div>
                  </td>
                  <td className="px-2 py-3.5 align-middle">
                    <span className="tabular-nums text-xs font-medium text-slate-700" dir="ltr">
                      {customerPhone(inv)}
                    </span>
                  </td>
                  <td className="px-2 py-3.5 text-center align-middle">
                    <span className="text-sm font-black tabular-nums text-indigo-950">
                      {formatToman(Number(inv.amount) || 0)}
                    </span>
                  </td>
                  <td className="px-2 py-3.5 text-center align-middle">
                    <span className="inline-flex rounded-lg bg-slate-100 px-2 py-0.5 text-[11px] font-bold text-slate-700 ring-1 ring-slate-200/80">
                      {getTypeText(inv.type)}
                    </span>
                  </td>
                  <td className="px-2 py-3.5 text-center align-middle">
                    <span
                      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold ${st.pill}`}
                    >
                      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${st.dot}`} aria-hidden />
                      {getStatusText(inv.status)}
                    </span>
                  </td>
                  <td className="px-2 py-3.5 align-middle text-xs font-medium tabular-nums text-slate-600">
                    {new Date(inv.createdAt).toLocaleDateString('fa-IR')}
                  </td>
                  {mode === 'payment-links' ? (
                    <td className="px-2 py-3.5 align-middle">
                      <span className="line-clamp-2 text-[11px] font-semibold text-violet-900">
                        {inv.salesPerson?.fullName || '—'}
                      </span>
                    </td>
                  ) : null}
                  <td className="px-2 py-3.5 text-center align-middle">
                    <button
                      type="button"
                      onClick={() => setSelectedInvoice(inv)}
                      className="rounded-lg border border-indigo-200 bg-indigo-50 px-2.5 py-1.5 text-[11px] font-bold text-indigo-800 transition hover:bg-indigo-100"
                    >
                      مشاهده
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  if (pageLoading && !hasLoadedOnceRef.current) {
    return (
      <div className="ios-fade-in space-y-5 rounded-2xl bg-slate-50/90 p-4 sm:p-6">
        <div className="rounded-2xl border border-indigo-100 bg-gradient-to-b from-indigo-50/60 to-white p-8 shadow-sm">
          <div className="mx-auto max-w-md text-center">
            <BrandSpinnerRing size="md" />
            <p className="mt-4 text-sm font-bold text-slate-700">در حال بارگذاری فاکتورها…</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ios-fade-in space-y-5 rounded-2xl bg-slate-50/90 p-4 sm:p-6">
      <div className="rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200/80 sm:p-6">
        <div className="flex flex-col gap-4 border-b border-slate-100 pb-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-indigo-700">
              <ReceiptText className="h-6 w-6 shrink-0" strokeWidth={2} aria-hidden />
              <p className="text-xs font-bold uppercase tracking-wide text-slate-500">مالی</p>
            </div>
            <h1 className="mt-1 text-2xl font-black tracking-tight text-slate-900 sm:text-3xl">فاکتورها و تراکنش‌ها</h1>
            <p className="mt-2 max-w-2xl text-sm font-medium leading-relaxed text-slate-600">
              رهگیری خرید دوره، لینک‌های فروشندگان و آمار عملکرد. همه مبالغ فاکتور به{' '}
              <span className="font-bold text-slate-800">تومان</span>؛ لینک پرداخت در بک‌اند به ریال ذخیره و در آمار
              فروشنده به تومان تبدیل می‌شود.
            </p>
          </div>
          <button
            type="button"
            onClick={() => void fetchData()}
            disabled={listRefreshing}
            className="inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-indigo-700 disabled:opacity-60"
          >
            <RefreshCw className={`h-4 w-4 ${listRefreshing ? 'animate-spin' : ''}`} aria-hidden />
            بروزرسانی
          </button>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          {tabs.map((tab) => {
            const Icon = tab.Icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`inline-flex items-center gap-2 rounded-full border px-4 py-2 text-sm font-bold shadow-sm transition active:scale-[0.98] ${
                  active
                    ? 'border-indigo-500 bg-indigo-600 text-white shadow-indigo-500/25'
                    : 'border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100'
                }`}
              >
                <Icon className="h-4 w-4 shrink-0 opacity-90" strokeWidth={2} aria-hidden />
                {tab.label}
                <span
                  className={`rounded-full px-2 py-0.5 text-[11px] font-black tabular-nums ${
                    active ? 'bg-white/20 text-white' : 'bg-white text-slate-700 ring-1 ring-slate-200/80'
                  }`}
                >
                  {tab.count.toLocaleString('fa-IR')}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-900 shadow-sm">
          {error}
        </div>
      ) : null}

      {activeTab !== 'salespersons' ? (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <div className="rounded-2xl border border-blue-100 bg-gradient-to-b from-blue-50 to-white p-4 shadow-sm">
              <p className="text-xs font-bold text-blue-800">کل ردیف (فیلتر)</p>
              <p className="mt-2 text-2xl font-black tabular-nums text-blue-950">{totalCount.toLocaleString('fa-IR')}</p>
              <p className="mt-1 text-[11px] font-semibold text-blue-800/85">در این تب</p>
            </div>
            <div className="rounded-2xl border border-emerald-100 bg-gradient-to-b from-emerald-50 to-white p-4 shadow-sm">
              <p className="text-xs font-bold text-emerald-800">پرداخت‌شده در این صفحه</p>
              <p className="mt-2 text-2xl font-black tabular-nums text-emerald-950">
                {pagePaidCount.toLocaleString('fa-IR')}
              </p>
              <p className="mt-1 text-[11px] font-semibold text-emerald-800/85">
                از {(activeTab === 'courses' ? courseRows.length : paymentRows.length).toLocaleString('fa-IR')} ردیف
                صفحه
              </p>
            </div>
            <div className="rounded-2xl border border-amber-100 bg-gradient-to-b from-amber-50 to-white p-4 shadow-sm">
              <p className="text-xs font-bold text-amber-900">در جریان / انتظار (صفحه)</p>
              <p className="mt-2 text-2xl font-black tabular-nums text-amber-950">
                {pagePendingCount.toLocaleString('fa-IR')}
              </p>
              <p className="mt-1 text-[11px] font-semibold text-amber-800/90">شامل در انتظار و درگاه</p>
            </div>
            <div className="rounded-2xl border border-indigo-100 bg-gradient-to-b from-indigo-50 to-white p-4 shadow-sm">
              <p className="text-xs font-bold text-indigo-800">جمع مبلغ پرداخت‌شده (صفحه)</p>
              <p className="mt-2 text-xl font-black tabular-nums text-indigo-950">{formatToman(pageVolumeToman)}</p>
              <p className="mt-1 text-[11px] font-semibold text-indigo-800/85">تومان · فقط ردیف‌های PAID همین صفحه</p>
            </div>
          </div>

          <div className="rounded-2xl bg-white p-4 shadow-sm ring-1 ring-slate-200/80 sm:p-5">
            <div className="flex flex-col gap-4 border-b border-slate-100 pb-4 lg:flex-row lg:items-end lg:justify-between">
              <div>
                <h2 className="text-base font-black text-slate-900">فیلتر و جستجوی سریع</h2>
                <p className="mt-1 text-xs font-medium text-slate-600">وضعیت پرداخت و در صورت لینک، فروشنده.</p>
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {activeFilterCount > 0 ? (
                  <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-600">
                    {activeFilterCount.toLocaleString('fa-IR')} فیلتر فعال
                  </span>
                ) : null}
                <button
                  type="button"
                  onClick={() => {
                    setStatusFilter('');
                    setSelectedSalesPerson('');
                    setCoursePage(1);
                    setPaymentLinkPage(1);
                  }}
                  className="rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-bold text-slate-700 transition hover:bg-slate-50"
                >
                  پاک‌کردن فیلترها
                </button>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              {STATUS_OPTIONS.map((opt) => (
                <button
                  key={opt.value || 'all'}
                  type="button"
                  onClick={() => setStatusFilter(opt.value)}
                  className={`rounded-full border px-3 py-1.5 text-xs font-bold transition ${
                    statusFilter === opt.value
                      ? 'border-indigo-500 bg-indigo-600 text-white shadow-sm'
                      : 'border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className={`${formInputCls} min-w-[12rem] sm:max-w-xs`}
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value || 'all'} value={o.value}>
                    {o.label === 'همه' ? 'همه وضعیت‌ها' : o.label}
                  </option>
                ))}
              </select>
              {activeTab === 'payment-links' ? (
                <select
                  value={selectedSalesPerson}
                  onChange={(e) => {
                    setSelectedSalesPerson(e.target.value);
                    setPaymentLinkPage(1);
                  }}
                  className={`${formInputCls} min-w-[12rem] flex-1 sm:max-w-md`}
                >
                  <option value="">همه فروشندگان</option>
                  {salesPersonRows.map((sp) => (
                    <option key={sp.salesPerson.id} value={sp.salesPerson.id}>
                      {sp.salesPerson.fullName}
                    </option>
                  ))}
                </select>
              ) : null}
            </div>
          </div>

          {activeTab === 'courses' ? (
            courseRows.length === 0 ? (
              <div className="rounded-2xl border border-slate-200/90 bg-white p-10 shadow-sm">
                <EmptyState
                  icon={<ReceiptText className="mx-auto h-10 w-10 text-slate-300" strokeWidth={1.5} />}
                  title="فاکتور دوره‌ای نیست"
                  description="با فیلتر فعلی یا هنوز خریدی ثبت نشده است."
                />
              </div>
            ) : (
              renderInvoiceTable(courseRows, 'courses')
            )
          ) : paymentRows.length === 0 ? (
            <div className="rounded-2xl border border-slate-200/90 bg-white p-10 shadow-sm">
              <EmptyState
                icon={<Link2 className="mx-auto h-10 w-10 text-slate-300" strokeWidth={1.5} />}
                title="فاکتور لینک فروش نیست"
                description="فروشنده یا لینک پرداختی با این فیلترها یافت نشد."
              />
            </div>
          ) : (
            renderInvoiceTable(paymentRows, 'payment-links')
          )}

          {renderPagination()}
        </>
      ) : (
        <div className="space-y-5">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <div className="rounded-2xl border border-indigo-100 bg-gradient-to-b from-indigo-50 to-white p-4 shadow-sm">
              <p className="text-xs font-bold text-indigo-800">فروشنده فعال</p>
              <p className="mt-2 text-2xl font-black text-indigo-950 tabular-nums">
                {Number(salesPersonsStats.summary?.totalSalesPersons || 0).toLocaleString('fa-IR')}
              </p>
            </div>
            <div className="rounded-2xl border border-violet-100 bg-gradient-to-b from-violet-50 to-white p-4 shadow-sm">
              <p className="text-xs font-bold text-violet-900">کل لینک‌ها</p>
              <p className="mt-2 text-2xl font-black text-violet-950 tabular-nums">
                {Number(salesPersonsStats.summary?.totalLinks || 0).toLocaleString('fa-IR')}
              </p>
            </div>
            <div className="rounded-2xl border border-emerald-100 bg-gradient-to-b from-emerald-50 to-white p-4 shadow-sm">
              <p className="text-xs font-bold text-emerald-800">پرداخت موفق</p>
              <p className="mt-2 text-2xl font-black text-emerald-950 tabular-nums">
                {Number(salesPersonsStats.summary?.totalPaidLinks || 0).toLocaleString('fa-IR')}
              </p>
            </div>
            <div className="rounded-2xl border border-amber-100 bg-gradient-to-b from-amber-50 to-white p-4 shadow-sm">
              <p className="text-xs font-bold text-amber-900">درآمد کل (تومان)</p>
              <p className="mt-2 text-xl font-black text-amber-950 tabular-nums">
                {formatToman(rialToToman(Number(salesPersonsStats.summary?.totalRevenue || 0)))}
              </p>
              <p className="mt-1 text-[11px] font-semibold text-amber-800/90">از لینک‌های پرداخت‌شده</p>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200/90 bg-white p-5 shadow-sm sm:p-6">
            <div className="mb-6 flex flex-wrap items-end justify-between gap-3 border-b border-slate-100 pb-4">
              <div>
                <h2 className="text-lg font-black text-slate-900">رتبه‌بندی فروشندگان</h2>
                <p className="mt-1 text-sm font-medium text-slate-600">
                  میانگین نرخ تبدیل کل:{' '}
                  <span className="font-black text-indigo-800">
                    {Number(salesPersonsStats.summary?.averageConversionRate || 0).toLocaleString('fa-IR')}%
                  </span>
                </p>
              </div>
              <div className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-800 ring-1 ring-emerald-200/80">
                <TrendingUp className="h-3.5 w-3.5" aria-hidden />
                بر اساس درآمد لینک
              </div>
            </div>

            {salesPersonRows.length === 0 ? (
              <EmptyState
                icon={<UsersRound className="mx-auto h-10 w-10 text-slate-300" strokeWidth={1.5} />}
                title="فروشنده‌ای نیست"
                description="کاربری با نقش فروشنده فعال در سیستم ثبت نشده است."
              />
            ) : (
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
                {salesPersonRows.map((sp, index) => (
                  <div
                    key={sp.salesPerson.id}
                    className="group relative overflow-hidden rounded-2xl border border-slate-100 bg-gradient-to-b from-white to-slate-50/60 p-5 shadow-sm ring-1 ring-slate-100 transition hover:border-indigo-200/80 hover:shadow-md"
                  >
                    <div className="pointer-events-none absolute inset-x-0 top-0 h-0.5 bg-gradient-to-l from-indigo-500 via-violet-500 to-rose-500 opacity-70" />
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex min-w-0 items-center gap-3">
                        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-600 to-violet-600 text-sm font-black text-white shadow-md">
                          {(index + 1).toLocaleString('fa-IR')}
                        </div>
                        <div className="min-w-0">
                          <p className="truncate text-base font-black text-slate-900">{sp.salesPerson.fullName}</p>
                          <p className="mt-0.5 text-xs font-semibold tabular-nums text-slate-500" dir="ltr">
                            {sp.salesPerson.phone}
                          </p>
                        </div>
                      </div>
                      <div className="shrink-0 text-left">
                        <p className="text-[10px] font-bold uppercase tracking-wide text-slate-500">درآمد</p>
                        <p className="text-lg font-black tabular-nums text-indigo-900">
                          {formatToman(rialToToman(Number(sp.statistics.totalRevenue) || 0))}
                        </p>
                        <p className="text-[10px] font-medium text-slate-500">تومان</p>
                      </div>
                    </div>
                    <div className="mt-4 grid grid-cols-3 gap-2 border-t border-slate-100 pt-4">
                      <div className="rounded-xl bg-white/80 px-2 py-2 text-center ring-1 ring-slate-100">
                        <p className="text-lg font-black text-slate-900 tabular-nums">
                          {sp.statistics.totalLinks.toLocaleString('fa-IR')}
                        </p>
                        <p className="text-[10px] font-bold text-slate-500">لینک</p>
                      </div>
                      <div className="rounded-xl bg-emerald-50/80 px-2 py-2 text-center ring-1 ring-emerald-100/80">
                        <p className="text-lg font-black text-emerald-900 tabular-nums">
                          {sp.statistics.paidLinks.toLocaleString('fa-IR')}
                        </p>
                        <p className="text-[10px] font-bold text-emerald-800">پرداخت</p>
                      </div>
                      <div className="rounded-xl bg-violet-50/80 px-2 py-2 text-center ring-1 ring-violet-100/80">
                        <p className="text-lg font-black text-violet-900 tabular-nums">
                          {sp.statistics.conversionRate.toLocaleString('fa-IR')}%
                        </p>
                        <p className="text-[10px] font-bold text-violet-800">تبدیل</p>
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap items-center justify-between gap-2 text-[11px] font-semibold text-slate-500">
                      <span>امروز: {sp.statistics.todayLinks.toLocaleString('fa-IR')} لینک</span>
                      {sp.statistics.lastActivity ? (
                        <span>آخرین فعالیت: {new Date(sp.statistics.lastActivity).toLocaleDateString('fa-IR')}</span>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <Modal
        isOpen={!!selectedInvoice}
        onClose={() => setSelectedInvoice(null)}
        title={selectedInvoice ? `فاکتور ${selectedInvoice.invoiceNumber}` : ''}
        size="large"
      >
        {selectedInvoice ? (
          <div className="space-y-5">
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-100 bg-slate-50/80 px-4 py-3">
              <div className="flex items-center gap-2">
                <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold ${getStatusStyle(selectedInvoice.status).pill}`}>
                  <span className={`h-1.5 w-1.5 rounded-full ${getStatusStyle(selectedInvoice.status).dot}`} />
                  {getStatusText(selectedInvoice.status)}
                </span>
                <span className="rounded-lg bg-white px-2 py-1 text-[11px] font-bold text-slate-700 ring-1 ring-slate-200/80">
                  {getTypeText(selectedInvoice.type)}
                </span>
              </div>
              <div className="text-left">
                <p className="text-[10px] font-bold text-slate-500">مبلغ</p>
                <p className="text-xl font-black tabular-nums text-indigo-950">
                  {formatToman(Number(selectedInvoice.amount) || 0)}{' '}
                  <span className="text-sm font-bold text-slate-600">تومان</span>
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
                <p className="text-xs font-bold text-slate-500">تاریخ ایجاد</p>
                <p className="mt-1 text-sm font-bold text-slate-900">
                  {new Date(selectedInvoice.createdAt).toLocaleString('fa-IR')}
                </p>
              </div>
              {selectedInvoice.paidAt ? (
                <div className="rounded-xl border border-slate-100 bg-white p-4 shadow-sm">
                  <p className="text-xs font-bold text-slate-500">تاریخ پرداخت</p>
                  <p className="mt-1 text-sm font-bold text-slate-900">
                    {new Date(selectedInvoice.paidAt).toLocaleString('fa-IR')}
                  </p>
                </div>
              ) : null}
            </div>

            <div className="rounded-xl border border-slate-100 bg-gradient-to-b from-slate-50/80 to-white p-4">
              <div className="mb-2 flex items-center justify-between gap-2">
                <h4 className="text-sm font-black text-slate-900">مشتری</h4>
                <button
                  type="button"
                  onClick={() => void handleCopyNumber(selectedInvoice.invoiceNumber)}
                  className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2 py-1 text-[11px] font-bold text-slate-700 transition hover:bg-slate-50"
                >
                  {copied ? <Check className="h-3.5 w-3.5 text-emerald-600" /> : <Copy className="h-3.5 w-3.5" />}
                  کپی شماره فاکتور
                </button>
              </div>
              <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
                <div>
                  <dt className="text-xs font-semibold text-slate-500">نام</dt>
                  <dd className="font-bold text-slate-900">{customerLabel(selectedInvoice)}</dd>
                </div>
                <div>
                  <dt className="text-xs font-semibold text-slate-500">موبایل</dt>
                  <dd className="font-mono font-semibold text-slate-800" dir="ltr">
                    {customerPhone(selectedInvoice)}
                  </dd>
                </div>
                {selectedInvoice.user?.email ? (
                  <div className="sm:col-span-2">
                    <dt className="text-xs font-semibold text-slate-500">ایمیل</dt>
                    <dd className="break-all font-medium text-slate-800">{selectedInvoice.user.email}</dd>
                  </div>
                ) : null}
              </dl>
            </div>

            {selectedInvoice.salesPerson ? (
              <div className="rounded-xl border border-violet-100 bg-violet-50/40 p-4">
                <h4 className="text-sm font-black text-violet-950">فروشنده</h4>
                <p className="mt-1 text-sm font-bold text-violet-900">{selectedInvoice.salesPerson.fullName}</p>
                <p className="text-xs font-semibold text-violet-800/90 tabular-nums" dir="ltr">
                  {selectedInvoice.salesPerson.phone}
                </p>
              </div>
            ) : null}

            {selectedInvoice.description ? (
              <div>
                <h4 className="mb-1 text-sm font-black text-slate-900">توضیحات</h4>
                <p className="rounded-xl border border-slate-100 bg-slate-50/80 p-3 text-sm leading-relaxed text-slate-700">
                  {selectedInvoice.description}
                </p>
              </div>
            ) : null}

            {selectedInvoice.paymentLink?.linkCode ? (
              <p className="text-xs font-semibold text-slate-600">
                کد لینک:{' '}
                <span className="rounded-md bg-slate-100 px-1.5 py-0.5 font-mono font-bold text-slate-900">
                  {selectedInvoice.paymentLink.linkCode}
                </span>
              </p>
            ) : null}

            <div className="flex flex-wrap justify-end gap-2 border-t border-slate-100 pt-4">
              <button
                type="button"
                onClick={() => setSelectedInvoice(null)}
                className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm font-bold text-slate-800 shadow-sm transition hover:bg-slate-50"
              >
                <X className="h-4 w-4" aria-hidden />
                بستن
              </button>
              <button
                type="button"
                onClick={() => {
                  window.print();
                }}
                className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-l from-indigo-600 to-violet-600 px-4 py-2.5 text-sm font-black text-white shadow-md transition hover:opacity-[0.96]"
              >
                <Printer className="h-4 w-4" aria-hidden />
                چاپ
              </button>
            </div>
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

export default Invoices;
